import type {
  ClientAnalytics,
  AnalyticsFilter, AnalyticsSummary, AnalyticsDistributions, AnalyticsDevices, AnalyticsDetail, AnalyticsOptions,
  AdminIdentity,
  AdminLoginResult,
  CatalogSource,
  CatalogSourceInput,
  DashboardSummary,
  FeaturePolicy,
  FeaturePolicyInput,
  ReleasePolicy,
  ReleasePolicyInput,
  RuntimeComponent,
  RuntimeComponentInput,
  SiteSettings,
  SiteSettingsInput,
  SiteDownload,
  SiteDownloadInput,
  TotpStatus,
} from "@player115/contracts";

function resolveApiBase() {
  const configured = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "");
  if (configured) return configured;
  if (typeof window === "undefined") return "";
  if (window.location.hostname === "admin.5bo.app") return "https://api.5bo.app";
  if (window.location.hostname === "staging-admin.5bo.app") return "https://staging-api.5bo.app";
  return "";
}

const baseUrl = resolveApiBase();
export const demoMode = import.meta.env.VITE_DEMO_MODE === "true";

type ApiErrorBody = { error?: { message?: string } };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    cache: "no-store",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ApiErrorBody;
    throw new Error(body.error?.message || `请求失败（${response.status}）`);
  }
  return response.json() as Promise<T>;
}

let demoAdmin: AdminIdentity = { id: "demo-admin", username: "admin", displayName: "admin", email: "vipaso@qq.com", passwordChangeRequired: false, totpEnabled: false };
let demoLoggedIn = false;
let demoReleases: ReleasePolicy[] = [{
  id: "demo-macos-arm64",
  platform: "macos",
  architecture: "aarch64",
  channel: "stable",
  latestVersion: "0.2.7",
  minSupportedVersion: "0.1.0",
  forceUpgrade: false,
  enforceAt: null,
  title: "115影音新版可用",
  releaseNotes: ["优化播放稳定性", "接入远程版本策略"],
  downloadPageUrl: "https://example.com/player115-download",
  backupDownloadPageUrl: null,
  accessCode: null,
  sha256: null,
  enabled: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}];
let demoFeatures: FeaturePolicy[] = [
  { featureKey: "cloud", displayName: "115 云端", description: "浏览和播放 115 云端媒体", mode: "open", lockedMessage: "当前账号暂未获得 115 云端模块权益", sortOrder: 10, updatedAt: new Date().toISOString() },
  { featureKey: "library", displayName: "影库", description: "媒体刮削、整理和海报墙", mode: "open", lockedMessage: "当前账号暂未获得影库模块权益", sortOrder: 20, updatedAt: new Date().toISOString() },
  { featureKey: "catalog", displayName: "片库", description: "影视资源站聚合与下载入口", mode: "open", lockedMessage: "当前账号暂未获得片库模块权益", sortOrder: 25, updatedAt: new Date().toISOString() },
  { featureKey: "music", displayName: "乐库", description: "音乐索引、封面和歌词能力", mode: "open", lockedMessage: "当前账号暂未获得乐库模块权益", sortOrder: 30, updatedAt: new Date().toISOString() },
  { featureKey: "ai_subtitles", displayName: "AI 字幕", description: "音视频字幕生成与处理", mode: "entitlement", lockedMessage: "AI 字幕属于增值模块，请先获取使用权益", sortOrder: 40, updatedAt: new Date().toISOString() },
];
let demoRuntimeComponents: RuntimeComponent[] = [{
  id: "demo-ai-runtime-windows-x64",
  componentKey: "ai_subtitles",
  displayName: "AI 字幕运行时",
  platform: "windows",
  architecture: "x86_64",
  channel: "stable",
  version: "1.0.0",
  downloadUrl: "https://example.com/FivePlayer-AI-Runtime_windows-x64_1.0.0.zip",
  sha256: "0277DF6DF368FA53DC6A7E5EBDD2CB0A46AF28C0CEB8F74667357E517B54CFE4",
  sizeBytes: 51274234,
  enabled: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}];
let demoCatalogSources: CatalogSource[] = [
  { sourceKey: "dytt8", displayName: "资源库 1", health: null, originalName: "电影天堂", feedUrl: "https://www.dytt8899.com/html/gndy/dyzz/index.html", homeUrl: "https://www.dytt8899.com/", ruleKey: "dytt8", enabled: true, sortOrder: 10, updatedAt: new Date().toISOString() },
  { sourceKey: "dy131", displayName: "资源库 2", health: null, originalName: "6V电影网", feedUrl: "https://www.dy131.org/dy/index.html", homeUrl: "https://www.dy131.org/", ruleKey: "dy131", enabled: true, sortOrder: 20, updatedAt: new Date().toISOString() },
  { sourceKey: "btbtla", displayName: "资源库 3", health: null, originalName: "BT联盟", feedUrl: "https://www.btbtla.com/type/movies/", homeUrl: "https://www.btbtla.com/", ruleKey: "btbtla", enabled: true, sortOrder: 30, updatedAt: new Date().toISOString() },
  { sourceKey: "mjf2020", displayName: "资源库 4", health: null, originalName: "美剧天堂", feedUrl: "https://www.meijutt.tv/gx.html", homeUrl: "https://www.meijutt.tv/", ruleKey: "mjf2020", enabled: true, sortOrder: 40, updatedAt: new Date().toISOString() },
];
let demoSiteSettings: SiteSettings = {
  heroCtaLabel: "即将开放",
  heroCtaHref: "#download",
  heroStatusLabel: "内部测试中",
  downloadTitle: "Five Player，正在准备中",
  downloadDescription: "当前版本仍在内部测试，暂不对外提供下载；按钮仅作预留，不连接下载地址。等播放、画面和稳定性准备好后再正式开放。",
  updatedAt: new Date().toISOString(),
};
let demoSiteDownloads: SiteDownload[] = [
  { platform: "macos", version: null, statusLabel: "暂未开放", downloadUrl: null, backupDownloadUrl: null, accessCode: null, published: false, updatedAt: new Date().toISOString() },
  { platform: "windows", version: null, statusLabel: "暂未开放", downloadUrl: null, backupDownloadUrl: null, accessCode: null, published: false, updatedAt: new Date().toISOString() },
  { platform: "android-tv", version: null, statusLabel: "暂未开放", downloadUrl: null, backupDownloadUrl: null, accessCode: null, published: false, updatedAt: new Date().toISOString() },
  { platform: "android-mobile", version: null, statusLabel: "暂未开放", downloadUrl: null, backupDownloadUrl: null, accessCode: null, published: false, updatedAt: new Date().toISOString() },
];

const demoDashboard: DashboardSummary = {
  clients: { total: 1286, active24h: 342, active7d: 817 },
  releases: { activePolicies: 2, forcedPolicies: 0 },
  features: { open: 3, entitlement: 1, disabled: 0 },
  versions: [
    { version: "0.2.7", count: 561 },
    { version: "0.2.6", count: 402 },
    { version: "0.1.0", count: 323 },
  ],
  recentAudit: [
    { id: "1", action: "feature.updated", targetType: "feature_policy", targetId: "ai_subtitles", actorName: "admin", createdAt: new Date(Date.now() - 18 * 60_000).toISOString() },
    { id: "2", action: "release.updated", targetType: "release_policy", targetId: "demo-macos-arm64", actorName: "admin", createdAt: new Date(Date.now() - 2 * 60 * 60_000).toISOString() },
  ],
};

function analyticsRequest<T>(path: string, f: AnalyticsFilter, signal?: AbortSignal) {
  const query = new URLSearchParams(Object.entries(f).map(([key,value])=>[key,String(value)]));
  const timeout=AbortSignal.timeout(15_000);
  return request<T>(`/api/v1/admin/analytics/${path}?${query}`, {signal:signal ? AbortSignal.any([signal,timeout]) : timeout});
}

export const api = {
  analyticsSummary: (f: AnalyticsFilter, signal?: AbortSignal) => analyticsRequest<AnalyticsSummary>('summary',f,signal),
  analyticsDistributions: (f: AnalyticsFilter, signal?: AbortSignal) => analyticsRequest<AnalyticsDistributions>('distributions',f,signal),
  analyticsDevices: (f: AnalyticsFilter, signal?: AbortSignal) => analyticsRequest<AnalyticsDevices>('devices',f,signal),
  analyticsDetail: (id: string, f: AnalyticsFilter, signal?: AbortSignal) => analyticsRequest<AnalyticsDetail>(`devices/${encodeURIComponent(id)}`,f,signal),
  analyticsOptions: () => request<AnalyticsOptions>('/api/v1/admin/analytics/options'),
  analyticsExport: (f: AnalyticsFilter) => request<{csv:string;count:number}>('/api/v1/admin/analytics/export',{method:'POST',body:JSON.stringify(f)}),
  analytics: () => request<ClientAnalytics>("/api/v1/admin/analytics"),
  async login(username: string, password: string) {
    if (demoMode) {
      if (!username || !password) throw new Error("请输入账号和密码");
      if (demoAdmin.totpEnabled) return { requiresTotp: true, challengeToken: "demo-totp-challenge", expiresInSeconds: 300 } satisfies AdminLoginResult;
      demoLoggedIn = true;
      return { requiresTotp: false, admin: demoAdmin } satisfies AdminLoginResult;
    }
    return request<AdminLoginResult>("/api/v1/admin/auth/login", { method: "POST", body: JSON.stringify({ username, password }) });
  },
  async verifyTotpLogin(challengeToken: string, code: string) {
    if (demoMode) {
      if (challengeToken !== "demo-totp-challenge" || code !== "123456") throw new Error("验证码无效或已过期");
      demoLoggedIn = true;
      return { admin: demoAdmin };
    }
    return request<{ admin: AdminIdentity }>("/api/v1/admin/auth/login/totp", {
      method: "POST",
      body: JSON.stringify({ challengeToken, code }),
    });
  },
  async me() {
    if (demoMode) {
      if (!demoLoggedIn) throw new Error("未登录");
      return { admin: demoAdmin };
    }
    return request<{ admin: AdminIdentity }>("/api/v1/admin/auth/me");
  },
  async changePassword(password: string) {
    if (demoMode) {
      if (password.length < 12) throw new Error("新密码至少需要 12 个字符");
      demoAdmin = { ...demoAdmin, passwordChangeRequired: false };
      return { admin: demoAdmin };
    }
    return request<{ admin: AdminIdentity }>("/api/v1/admin/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },
  async totpStatus() {
    if (demoMode) return { enabled: demoAdmin.totpEnabled, enabledAt: null, recoveryCodesRemaining: demoAdmin.totpEnabled ? 8 : 0 } satisfies TotpStatus;
    return request<TotpStatus>("/api/v1/admin/auth/totp/status");
  },
  async setupTotp(password: string) {
    if (demoMode) {
      if (!password) throw new Error("请输入当前密码");
      return { secret: "JBSWY3DPEHPK3PXP", qrCodeDataUrl: "" };
    }
    return request<{ secret: string; qrCodeDataUrl: string }>("/api/v1/admin/auth/totp/setup", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },
  async confirmTotp(code: string) {
    if (demoMode) {
      if (code !== "123456") throw new Error("演示模式验证码为 123456");
      demoAdmin = { ...demoAdmin, totpEnabled: true };
      return { admin: demoAdmin, recoveryCodes: ["DEMO-1111-2222-3333", "DEMO-4444-5555-6666"] };
    }
    return request<{ admin: AdminIdentity; recoveryCodes: string[] }>("/api/v1/admin/auth/totp/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },
  async disableTotp(password: string, code: string) {
    if (demoMode) {
      if (!password || code !== "123456") throw new Error("演示模式验证码为 123456");
      demoAdmin = { ...demoAdmin, totpEnabled: false };
      return { admin: demoAdmin };
    }
    return request<{ admin: AdminIdentity }>("/api/v1/admin/auth/totp/disable", {
      method: "POST",
      body: JSON.stringify({ password, code }),
    });
  },
  async logout() {
    if (demoMode) {
      demoLoggedIn = false;
      return { ok: true };
    }
    return request<{ ok: boolean }>("/api/v1/admin/auth/logout", { method: "POST" });
  },
  async dashboard() {
    if (demoMode) return structuredClone(demoDashboard);
    return request<DashboardSummary>("/api/v1/admin/dashboard");
  },
  async releases() {
    if (demoMode) return { releases: structuredClone(demoReleases) };
    return request<{ releases: ReleasePolicy[] }>("/api/v1/admin/releases");
  },
  async saveRelease(input: ReleasePolicyInput) {
    if (demoMode) {
      const existing = demoReleases.find((release) => release.platform === input.platform && release.architecture === input.architecture && release.channel === input.channel);
      const release: ReleasePolicy = { ...input, id: existing?.id ?? crypto.randomUUID(), createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
      demoReleases = [...demoReleases.filter((item) => item.id !== release.id), release];
      return { release };
    }
    return request<{ release: ReleasePolicy }>("/api/v1/admin/releases", { method: "PUT", body: JSON.stringify(input) });
  },
  async siteSettings() {
    if (demoMode) return { settings: structuredClone(demoSiteSettings) };
    return request<{ settings: SiteSettings }>("/api/v1/admin/site-settings");
  },
  async saveSiteSettings(input: SiteSettingsInput) {
    if (demoMode) {
      demoSiteSettings = { ...input, updatedAt: new Date().toISOString() };
      return { settings: structuredClone(demoSiteSettings) };
    }
    return request<{ settings: SiteSettings }>("/api/v1/admin/site-settings", { method: "PUT", body: JSON.stringify(input) });
  },
  async siteDownloads() {
    if (demoMode) return { downloads: structuredClone(demoSiteDownloads) };
    return request<{ downloads: SiteDownload[] }>("/api/v1/admin/site-downloads");
  },
  async saveSiteDownload(input: SiteDownloadInput) {
    if (demoMode) {
      const download: SiteDownload = { ...input, updatedAt: new Date().toISOString() };
      demoSiteDownloads = demoSiteDownloads.map((item) => item.platform === input.platform ? download : item);
      return { download: structuredClone(download) };
    }
    return request<{ download: SiteDownload }>(`/api/v1/admin/site-downloads/${input.platform}`, { method: "PUT", body: JSON.stringify(input) });
  },
  async runtimeComponents() {
    if (demoMode) return { components: structuredClone(demoRuntimeComponents) };
    return request<{ components: RuntimeComponent[] }>("/api/v1/admin/runtime-components");
  },
  async saveRuntimeComponent(input: RuntimeComponentInput) {
    if (demoMode) {
      const existing = demoRuntimeComponents.find((item) => item.componentKey === input.componentKey && item.platform === input.platform && item.architecture === input.architecture && item.channel === input.channel);
      const component: RuntimeComponent = { ...input, id: existing?.id ?? crypto.randomUUID(), createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
      demoRuntimeComponents = [...demoRuntimeComponents.filter((item) => item.id !== component.id), component];
      return { component };
    }
    return request<{ component: RuntimeComponent }>("/api/v1/admin/runtime-components", { method: "PUT", body: JSON.stringify(input) });
  },
  async features() {
    if (demoMode) return { features: structuredClone(demoFeatures) };
    return request<{ features: FeaturePolicy[] }>("/api/v1/admin/features");
  },
  async saveFeature(input: FeaturePolicyInput) {
    if (demoMode) {
      const feature: FeaturePolicy = { ...input, updatedAt: new Date().toISOString() };
      demoFeatures = demoFeatures.map((item) => item.featureKey === feature.featureKey ? feature : item);
      return { feature };
    }
    return request<{ feature: FeaturePolicy }>(`/api/v1/admin/features/${encodeURIComponent(input.featureKey)}`, { method: "PUT", body: JSON.stringify(input) });
  },
  async checkCatalogSource(source: CatalogSource) {
    if (demoMode) throw new Error("演示模式不执行真实源站访问检测");
    return request<{ source: CatalogSource }>("/api/v1/admin/catalog-sources/" + encodeURIComponent(source.sourceKey) + "/check", {
      method: "POST", body: JSON.stringify({feedUrl:source.feedUrl,homeUrl:source.homeUrl}),
    });
  },
  async catalogSources() {
    if (demoMode) return { sources: structuredClone(demoCatalogSources) };
    return request<{ sources: CatalogSource[] }>("/api/v1/admin/catalog-sources");
  },
  async saveCatalogSource(input: CatalogSourceInput) {
    if (demoMode) {
      const source: CatalogSource = { ...input, displayName: input.displayName || "资源库", health: null, updatedAt: new Date().toISOString() };
      demoCatalogSources = demoCatalogSources.map((item) => item.sourceKey === source.sourceKey ? source : item);
      return { source };
    }
    return request<{ source: CatalogSource }>(`/api/v1/admin/catalog-sources/${encodeURIComponent(input.sourceKey)}`, { method: "PUT", body: JSON.stringify(input) });
  },
  async createCatalogSource(input: CatalogSourceInput) {
    if (demoMode) {
      const source: CatalogSource = { ...input, displayName: input.displayName || "资源库", health: null, updatedAt: new Date().toISOString() };
      demoCatalogSources = [...demoCatalogSources, source];
      return { source };
    }
    return request<{ source: CatalogSource }>("/api/v1/admin/catalog-sources", { method: "POST", body: JSON.stringify(input) });
  },
  async deleteCatalogSource(sourceKey: string) {
    if (demoMode) {
      demoCatalogSources = demoCatalogSources.filter((item) => item.sourceKey !== sourceKey);
      return;
    }
    await request<void>(`/api/v1/admin/catalog-sources/${encodeURIComponent(sourceKey)}`, { method: "DELETE" });
  },
};
